import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Package, Plus, Minus, ShoppingCart } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { getImageUrl } from "../../utils/imageUtils";
import { useInfinityCustomer } from "../../context/InfinityCustomerContext";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Right-hand slide-out cart (TR-8 2.4.1.3), modeled on the edisplayroom CartSheet.
 * Opens via cartOpen; auto-opens when an item is added. Proceeds to checkout.
 */
export default function InfinityCartSheet() {
  const { http, cart, cartOpen, setCartOpen, updateQty, removeItem, theme, basePath } = useInfinityCustomer();
  const navigate = useNavigate();
  const [charges, setCharges] = useState({ engraving_charge: 0 });

  useEffect(() => {
    if (cartOpen) http.get("/charges").then((r) => setCharges(r.data || {})).catch(() => {});
  }, [cartOpen, http]);

  // Per-unit price = product display + any customization add-on (TR-9 3.3).
  const unitPrice = (it) => Number(it.product.display_price ?? it.product.base_price ?? 0) + Number(it.customization_total || 0);

  const totals = useMemo(() => {
    let products = 0, engraving = 0;
    for (const it of cart) {
      products += unitPrice(it) * it.quantity;
      engraving += (it.engraving_lines?.length || 0) * (charges.engraving_charge || 0) * it.quantity;
    }
    return { subtotal: products + engraving };
  }, [cart, charges]);

  const checkout = () => {
    setCartOpen(false);
    navigate(`${basePath}/checkout`);
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart size={18} /> Your Cart
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Your cart is empty.</div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto -mx-6 px-6 divide-y">
              {cart.map((it) => {
                const price = unitPrice(it);
                return (
                  <div key={it.key} className="py-3 flex items-start gap-3">
                    <div className="w-14 h-14 rounded-lg bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                      {it.product.image_url ? <img src={getImageUrl(it.product.image_url)} alt={it.product.name} className="w-full h-full object-contain" /> : <Package className="w-5 h-5 text-slate-300" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 text-sm">{it.product.name}</p>
                      {it.color ? <p className="text-xs text-slate-500">Color: {it.color}</p> : null}
                      {it.engraving_lines?.length ? <p className="text-xs text-slate-500">Engraving: {it.engraving_lines.join(" / ")}</p> : null}
                      {(it.customizations || []).map((c) => (
                        <p key={c.customization_id} className="text-xs text-slate-500">{c.name}: {c.fields.map((f) => f.value).filter((v) => v && v !== true).join(", ")}</p>
                      ))}
                      {it.custom_image_url ? (
                        <div className="flex items-center gap-1 mt-1">
                          <img src={getImageUrl(it.custom_image_url)} alt="Print" className="w-6 h-6 rounded object-cover border" />
                          <span className="text-[10px] text-slate-400">print attached</span>
                        </div>
                      ) : null}
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQty(it.key, -1)} className="w-6 h-6 rounded border flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <span className="text-sm w-5 text-center">{it.quantity}</span>
                        <button onClick={() => updateQty(it.key, 1)} className="w-6 h-6 rounded border flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-800 text-sm">{money(price * it.quantity)}</p>
                      <button onClick={() => removeItem(it.key)} className="text-red-500 mt-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-bold text-slate-900">{money(totals.subtotal)}</span>
              </div>
              <p className="text-xs text-slate-400">Shipping calculated at checkout.</p>
              <Button className="w-full text-white" style={{ backgroundColor: theme.primary_color || "#2E72BC" }} onClick={checkout}>
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
